/* Variant slider — centres the selected option on load, drives the arrows,
   and adds mouse drag-to-scroll. Shopify re-renders the section on variant
   change, so connectedCallback runs again each time. */

const DRAG_THRESHOLD = 5; // px before a press becomes a drag rather than a click
const FRICTION = 0.94;
const MIN_VELOCITY = 0.15;

class VariantSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;

    this.prevBtn = this.querySelector('[data-slider-prev]');
    this.nextBtn = this.querySelector('[data-slider-next]');

    this.pointerId = null;
    this.isDragging = false;
    this.didDrag = false;
    this.velocity = 0;
    this.momentumFrame = null;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.onDragStart = this.onDragStart.bind(this);

    this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);

    this.track.addEventListener('pointerdown', this.onPointerDown);
    this.track.addEventListener('pointermove', this.onPointerMove);
    this.track.addEventListener('pointerup', this.onPointerUp);
    this.track.addEventListener('pointercancel', this.onPointerUp);
    this.track.addEventListener('dragstart', this.onDragStart);
    // Capture phase: this runs before the label's own click handler, which is
    // the only reliable way to swallow the click that follows a drag.
    this.track.addEventListener('click', this.onClickCapture, true);

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

    this.handleScroll();

    if (this.dataset.scrollToSelected === 'true') {
      requestAnimationFrame(() => this.centerSelected());
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    this.stopMomentum();
  }

  get isScrollable() {
    if (!this.track) return false;
    if (this.dataset.mobileOnly === 'true' && window.matchMedia('(min-width: 750px)').matches) {
      return false;
    }
    return this.track.scrollWidth - this.track.clientWidth > 2;
  }

  get isRtl() {
    return this.dataset.rtl === 'true';
  }

  /* Both directions expressed as bounds, so the rest of the code stays
     direction-agnostic instead of branching on isRtl everywhere. */
  get scrollBounds() {
    const distance = this.track.scrollWidth - this.track.clientWidth;
    return this.isRtl ? { min: -distance, max: 0 } : { min: 0, max: distance };
  }  

  handleResize() {
    this.handleScroll();
  }

  handleScroll() {
    if (!this.track) return;

    const scrollable = this.isScrollable;
    this.track.dataset.draggable = scrollable ? 'true' : 'false';

    if (!scrollable) {
      this.toggleArrow(this.prevBtn, false);
      this.toggleArrow(this.nextBtn, false);
      return;
    }

    const { min, max } = this.scrollBounds;
    const left = this.track.scrollLeft;

    this.toggleArrow(this.prevBtn, left > min + 4);
    this.toggleArrow(this.nextBtn, left < max - 4);
  }

  toggleArrow(button, visible) {
    if (!button) return;
    button.hidden = !visible;
  }

  /* --- Mouse drag ------------------------------------------------------ */

  onPointerDown(event) {
    // Touch and pen keep native scrolling, which is smoother than anything
    // we could reimplement. Drag is a mouse-only affordance.
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (!this.isScrollable) return;

    this.stopMomentum();

    this.pointerId = event.pointerId;
    this.isDragging = false;
    this.didDrag = false;
    this.startX = event.clientX;
    this.startScroll = this.track.scrollLeft;
    this.lastX = event.clientX;
    this.lastTime = performance.now();
    this.velocity = 0;
  }

  onPointerMove(event) {
    if (this.pointerId === null || event.pointerId !== this.pointerId) return;

    const delta = event.clientX - this.startX;

    if (!this.isDragging) {
      if (Math.abs(delta) < DRAG_THRESHOLD) return;
      this.isDragging = true;
      this.didDrag = true;
      this.track.dataset.dragging = 'true';
      // Capture keeps events flowing even if the cursor leaves the rail,
      // so a drag never gets stuck in a half-finished state.
      this.track.setPointerCapture(this.pointerId);
    }

    event.preventDefault();

    const now = performance.now();
    const elapsed = now - this.lastTime;

    if (elapsed > 0) {
      this.velocity = (event.clientX - this.lastX) / elapsed;
      this.lastX = event.clientX;
      this.lastTime = now;
    }

    this.track.scrollLeft = this.startScroll - delta;
  }

  onPointerUp(event) {
    if (this.pointerId === null || event.pointerId !== this.pointerId) return;

    if (this.track.hasPointerCapture(this.pointerId)) {
      this.track.releasePointerCapture(this.pointerId);
    }

    const wasDragging = this.isDragging;

    this.pointerId = null;
    this.isDragging = false;

    if (!wasDragging) {
      this.didDrag = false;
      return;
    }

    // Stale flick: the pointer sat still before release, so no momentum.
    if (performance.now() - this.lastTime > 100) this.velocity = 0;

    if (Math.abs(this.velocity) > MIN_VELOCITY) {
      this.startMomentum();
    } else {
      this.endDrag();
    }
  }

  startMomentum() {
    const stepFrame = () => {
      this.velocity *= FRICTION;

      if (Math.abs(this.velocity) < MIN_VELOCITY) {
        this.endDrag();
        return;
      }

      const { min, max } = this.scrollBounds;
      const next = this.track.scrollLeft - this.velocity * 16;

      if (next <= min || next >= max) {
        this.track.scrollLeft = next <= min ? min : max;
        this.endDrag();
        return;
      }

      this.track.scrollLeft = next;
      this.momentumFrame = requestAnimationFrame(stepFrame);
    };

    this.momentumFrame = requestAnimationFrame(stepFrame);
  }

  stopMomentum() {
    if (this.momentumFrame) {
      cancelAnimationFrame(this.momentumFrame);
      this.momentumFrame = null;
    }
  }

  endDrag() {
    this.stopMomentum();
    this.velocity = 0;
    // Snap and smooth scrolling come back only once movement has settled,
    // otherwise they fight the drag and the rail stutters.
    delete this.track.dataset.dragging;
    this.handleScroll();
  }

  onClickCapture(event) {
    if (!this.didDrag) return;
    event.preventDefault();
    event.stopPropagation();
    this.didDrag = false;
  }

  onDragStart(event) {
    event.preventDefault();
  }

  /* --- Arrows and positioning ------------------------------------------ */

  /* Scroll by roughly one tile less than a full viewport, so the customer
     keeps one option in common between the two views and never loses context. */
  step(direction) {
    if (!this.track) return;
    this.stopMomentum();

    const label = this.track.querySelector('label');
    const tile = label ? label.offsetWidth + 6 : 110;
    const amount = Math.max(this.track.clientWidth - tile, tile);

    this.track.scrollBy({ left: amount * direction, behavior: 'smooth' });
  }

  centerSelected() {
    if (!this.track || !this.isScrollable) return;

    const checked = this.track.querySelector('input[type="radio"]:checked');
    if (!checked || !checked.id) return;

    const label = this.track.querySelector('label[for="' + checked.id + '"]');
    if (!label) return;

    // Rects, not offsets: this measures the tile relative to what is on
    // screen right now, which holds in either direction.
    const trackRect = this.track.getBoundingClientRect();
    const labelRect = label.getBoundingClientRect();
    const offset = labelRect.left - trackRect.left;

    // Already fully visible: leave the scroll position alone.
    if (offset >= 0 && offset + labelRect.width <= this.track.clientWidth) {
      this.handleScroll();
      return;
    }

    const { min, max } = this.scrollBounds;
    const target =
      this.track.scrollLeft + offset - (this.track.clientWidth - labelRect.width) / 2;
    const previous = this.track.style.scrollBehavior;

    // 'auto' on purpose: a smooth scroll on page load reads as a glitch.
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollLeft = Math.min(Math.max(target, min), max);
    this.track.style.scrollBehavior = previous;

    this.handleScroll();
  }
}

if (!customElements.get('variant-slider')) {
  customElements.define('variant-slider', VariantSlider);
}